import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Shield, Loader2, Eye, EyeOff } from 'lucide-react'
import { supabase } from '../../lib/supabase'

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'

export default function AdminLoginPage() {
  const navigate = useNavigate()
  const [email,    setEmail]    = useState('')
  const [password, setPassword] = useState('')
  const [showPw,   setShowPw]   = useState(false)
  const [loading,  setLoading]  = useState(false)
  const [error,    setError]    = useState('')

  const handleLogin = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      const { data, error: authErr } = await supabase.auth.signInWithPassword({ email, password })
      if (authErr) throw authErr

      // Ask the server whether this account is an admin. The old check used
      // VITE_ADMIN_EMAILS, which was unset — and an unset list meant "admit
      // everyone", so the admin login accepted any valid user account.
      const userEmail = data.session?.user?.email || ''
      const r = await fetch(`${API_BASE}/admin/me`, {
        headers: { Authorization: `Bearer ${data.session?.access_token}` },
      })
      if (!r.ok) {
        await supabase.auth.signOut()
        throw new Error(`${userEmail} is not an admin account.`)
      }

      navigate('/admin', { replace: true })
    } catch (err) {
      setError(err.message || 'Login failed')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4"
      style={{
        background: '#0f172a',
        backgroundImage: `linear-gradient(rgba(255,255,255,0.015) 1px, transparent 1px),
          linear-gradient(90deg, rgba(255,255,255,0.015) 1px, transparent 1px)`,
        backgroundSize: '40px 40px',
      }}>
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="flex flex-col items-center mb-8">
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4"
            style={{ background: 'rgba(220,38,38,0.15)', border: '1px solid rgba(220,38,38,0.3)' }}>
            <Shield style={{ width: 22, height: 22, color: '#f87171' }} />
          </div>
          <h1 className="font-sans text-xl font-bold" style={{ color: '#f1f5f9' }}>AcdyON Admin</h1>
          <p className="font-mono text-xs mt-1" style={{ color: '#475569', letterSpacing: '0.1em' }}>RESTRICTED ACCESS</p>
        </div>

        {/* Card */}
        <form onSubmit={handleLogin}
          className="rounded-2xl p-6 space-y-4"
          style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>

          {error && (
            <div className="px-4 py-3 rounded-xl font-sans text-sm"
              style={{ background: 'rgba(220,38,38,0.12)', border: '1px solid rgba(220,38,38,0.3)', color: '#fca5a5' }}>
              {error}
            </div>
          )}

          <div>
            <label className="block font-sans text-xs font-semibold mb-1.5"
              style={{ color: '#64748b', letterSpacing: '0.05em' }}>EMAIL</label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="admin@acdyon.com"
              required
              autoFocus
              className="w-full rounded-xl px-4 py-2.5 font-sans text-sm outline-none transition-all"
              style={{
                background: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.1)',
                color: '#f1f5f9',
              }}
              onFocus={e => e.target.style.borderColor = 'rgba(239,68,68,0.5)'}
              onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.1)'}
            />
          </div>

          <div>
            <label className="block font-sans text-xs font-semibold mb-1.5"
              style={{ color: '#64748b', letterSpacing: '0.05em' }}>PASSWORD</label>
            <div className="relative">
              <input
                type={showPw ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                className="w-full rounded-xl px-4 py-2.5 pr-10 font-sans text-sm outline-none transition-all"
                style={{
                  background: 'rgba(255,255,255,0.06)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  color: '#f1f5f9',
                }}
                onFocus={e => e.target.style.borderColor = 'rgba(239,68,68,0.5)'}
                onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.1)'}
              />
              <button type="button" onClick={() => setShowPw(p => !p)}
                className="absolute right-3 top-1/2 -translate-y-1/2"
                style={{ color: '#475569' }}>
                {showPw
                  ? <EyeOff style={{ width: 15, height: 15 }} />
                  : <Eye style={{ width: 15, height: 15 }} />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading || !email || !password}
            className="w-full py-2.5 rounded-xl font-sans text-sm font-semibold transition-all mt-2"
            style={{
              background: loading || !email || !password ? 'rgba(220,38,38,0.3)' : '#dc2626',
              color: '#fff',
              opacity: loading || !email || !password ? 0.6 : 1,
            }}>
            {loading
              ? <span className="flex items-center justify-center gap-2">
                  <Loader2 style={{ width: 14, height: 14, animation: 'spin 1s linear infinite' }} />
                  Authenticating…
                </span>
              : 'Sign in to Admin'}
          </button>
        </form>

        <p className="text-center font-sans text-xs mt-6" style={{ color: '#334155' }}>
          Not an admin?{' '}
          <a href="/app/dashboard" className="underline" style={{ color: '#475569' }}>Go to student app</a>
        </p>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  )
}
